import compact from 'lodash/compact'
import {canonicalizeStore} from '../shared/canonicalize'
import Col from 'react-bootstrap/lib/Col'
import Link from 'react-router/lib/Link'
import ListGroup from './listGroup'
import PropTypes from 'prop-types'
import React from 'react'
import Row from 'react-bootstrap/lib/Row'
import {colors} from '../styles'
import {css} from 'glamor'
import JsonLD from './jsonLD'
import {storeDisplayNames, storeTypes} from '../constants/storeNavigation'

const style = {
  breadcrumb: css({
    margin: 0,
    padding: 0
  }),
  desktop: css({
    background: 'none',
    listStyle: 'none',
    margin: '16px 0'
  }),
  mobile: css({
    padding: 0,
    '> a': {
      borderLeft: 'none',
      borderRight: 'none',
      borderTop: 'none',
      color: colors.sanMarinoBlue
    }
  }),
  item: css({
    display: 'inline-block',
    '& + li:before': {
      color: colors.headingText,
      content: '/',
      margin: '0 6px'
    }
  }),
  itemActive: css({
    color: colors.headingText
  })
}

function format(string, replacer = '_') {
  return string.replace(/^a-zA-Z0-9_\//g, replacer).toLowerCase()
}

function parseChannelBreadcrumbs({
  channel,
  citylist: city,
  conferencelist: conference,
  country,
  untyped: untyped,
  leaguelist: league,
  statelist: state
}) {
  let breadcrumbs = [
    {
      DisplayName: channel.name,
      Url: format(`/${channel.value}_directory`)
    }
  ]

  if (untyped) {
    untyped.forEach((breadcrumb) => {
      breadcrumbs.push({
        DisplayName: breadcrumb.name,
        Url: format(
          `${breadcrumbs[breadcrumbs.length - 1].Url}/${breadcrumb.value}`
        )
      })
    })
  }

  ;[country, conference, league, state, city].forEach((breadcrumb) => {
    if (breadcrumb) {
      breadcrumbs.push({
        DisplayName: breadcrumb.name,
        Url: format(
          `${breadcrumbs[breadcrumbs.length - 1].Url}/${breadcrumb.value}`
        )
      })
    }
  })

  return breadcrumbs
}

function parseProductBreadcrumbs(product) {
  return [
    {
      DisplayName: product.ProductName
    }
  ]
}

function parseStoreBreadcrumbs(store) {
  const {
    CatalogKey,
    Channel,
    City,
    Country,
    CountryCode,
    LeagueName,
    Sport,
    State,
    StoreName,
    StoreType
  } = store

  const breadcrumbs = [
    {
      DisplayName: storeDisplayNames[StoreType],
      Url: format(`/${StoreType}_directory`)
    }
  ]

  if (State === 'Mascots') {
    // Mascot stores use a fake Seattle,Mascots City/State so we can't link
    // back to a non-existent state.
    breadcrumbs.push({
      DisplayName: State,
      Url: format(`/${StoreType}_directory/${State}`)
    })
  } else if (StoreType === storeTypes.PROSPORTS) {
    breadcrumbs.push(
      {
        DisplayName: Sport,
        Url: format(`/${StoreType}_directory/${Sport}`)
      },
      {
        DisplayName: Channel.Name,
        Url: format(`/${StoreType}_directory/${Sport}/${Channel.Tag}`)
      },
      {
        DisplayName: LeagueName,
        Url: format(
          `/${StoreType}_directory/${Sport}/${Channel.Tag}/${LeagueName}`
        )
      },
      {
        DisplayName: StoreName,
        Url: canonicalizeStore(store)
      }
    )
  } else if (StoreType === storeTypes.DESTINATION) {
    breadcrumbs.push(
      {
        DisplayName: Channel.Name,
        Url: format(`/${StoreType}_directory/${Channel.Tag}`)
      },
      CatalogKey === 'cities' || CatalogKey === 'counties'
        ? {
            DisplayName: State,
            Url: format(`/${StoreType}_directory/${Channel.Tag}/${State}`)
          }
        : null,
      {
        DisplayName: StoreName,
        Url: canonicalizeStore(store)
      }
    )
  } else if (StoreType === storeTypes.COLLEGE) {
    breadcrumbs.push(
      {
        DisplayName: (Country && Country.Country) || CountryCode.toUpperCase(),
        Url: format(`/${StoreType}_directory/${CountryCode}`)
      },
      {
        DisplayName: State,
        Url: format(`/${StoreType}_directory/${CountryCode}/${State}`)
      }
    )
    breadcrumbs.push({
      DisplayName: StoreName,
      Url: canonicalizeStore(store)
    })
  } else {
    breadcrumbs.push(
      {
        DisplayName: (Country && Country.Country) || CountryCode.toUpperCase(),
        Url: format(`/${StoreType}_directory/${CountryCode}`)
      },
      {
        DisplayName: State,
        Url: format(`/${StoreType}_directory/${CountryCode}/${State}`)
      }
    )
    if (StoreType === storeTypes.SCHOOL) {
      breadcrumbs.push({
        DisplayName: City,
        Url: format(`/${StoreType}_directory/${CountryCode}/${State}/${City}`)
      })
    }
    breadcrumbs.push({
      DisplayName: StoreName,
      Url: canonicalizeStore(store)
    })
  }

  return breadcrumbs.filter((b) => b)
}

const Breadcrumb = ({channel, store, product, items, ...props}) => {
  let breadcrumbs
  if (items) {
    breadcrumbs = items
  } else {
    breadcrumbs = []

    if (channel) {
      breadcrumbs = breadcrumbs.concat(
        parseChannelBreadcrumbs({channel, ...props})
      )
    }

    if (store) {
      breadcrumbs = breadcrumbs.concat(parseStoreBreadcrumbs(store))
    }

    if (product) {
      breadcrumbs = breadcrumbs.concat(parseProductBreadcrumbs(product))
    }
  }

  const itemListElement = compact(
    breadcrumbs.map((crumb, index) => {
      return {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@id': `${crumb.Url}`,
          name: crumb.DisplayName
        }
      }
    })
  )

  return (
    <Row className="breadcrumbs" {...props}>
      <Col xsHidden smHidden md={12} {...style.desktop}>
        <JsonLD data={{'@type': 'BreadcrumbList', itemListElement}} />
        <nav aria-label="breadcrumb">
          <ol role="navigation" {...style.breadcrumb}>
            {breadcrumbs.map((crumb, index, crumbArray) => {
              if (index !== crumbArray.length - 1) {
                return (
                  <Breadcrumb.Item key={index} href={crumb.Url}>
                    {crumb.DisplayName}
                  </Breadcrumb.Item>
                )
              }

              return (
                <Breadcrumb.Item key={index} active>
                  {crumb.DisplayName}
                </Breadcrumb.Item>
              )
            })}
          </ol>
        </nav>
      </Col>
      <Col xs={12} mdHidden lgHidden {...style.mobile}>
        <ListGroup.Heading to={breadcrumbs[breadcrumbs.length - 2].Url}>
          {breadcrumbs[breadcrumbs.length - 2].DisplayName}
        </ListGroup.Heading>
      </Col>
    </Row>
  )
}

Breadcrumb.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      DisplayName: PropTypes.string.isRequired,
      Url: PropTypes.string
    })
  ),
  channel: PropTypes.object,
  product: PropTypes.object,
  store: PropTypes.object
}

Breadcrumb.Item = ({active, children, href}) => (
  <li aria-current={active ? 'page' : undefined} {...style.item}>
    {active ? (
      <span {...style.itemActive}>{children}</span>
    ) : (
      <Link to={href}>{children}</Link>
    )}
  </li>
)

Breadcrumb.Item.displayName = 'Breadcrumb.Item'
Breadcrumb.Item.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node.isRequired,
  href: PropTypes.string
}

Breadcrumb.Item.defaultProps = {
  active: false
}

export default Breadcrumb
